import type { RecordingListItem } from "../api/types";

// "Name" filter shared by the Fraud and Recordings pages. The Fraud page derives options from
// its loaded recordings (uid -> resolved display name); the paginated Recordings page gets
// them from GET /api/recordings/summary, where the same rules run server-side. Recordings
// whose uploader can't be resolved (no uid, deleted or nameless account) are "Unknown".

export const UNKNOWN_UPLOADER = "__unknown__";

export interface UploaderOption {
  uid: string;
  name: string;
}

/** Unique (uid, name) pairs from the items, sorted by name; includes Unknown when present. */
export function uploaderOptions(items: RecordingListItem[]): UploaderOption[] {
  const byUid = new Map<string, string>();
  let hasUnknown = false;
  for (const r of items) {
    if (r.user_name && r.user_uid) byUid.set(r.user_uid, r.user_name);
    else hasUnknown = true;
  }
  const options = [...byUid.entries()]
    .map(([uid, name]) => ({ uid, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  if (hasUnknown) options.push({ uid: UNKNOWN_UPLOADER, name: "Unknown" });
  return options;
}

/** Apply the selected uploader uid ("" = all) to the items. */
export function filterByUploader(
  items: RecordingListItem[],
  uploader: string,
): RecordingListItem[] {
  if (!uploader) return items;
  if (uploader === UNKNOWN_UPLOADER) return items.filter((r) => !(r.user_name && r.user_uid));
  return items.filter((r) => r.user_uid === uploader);
}

export default function UploaderFilter({
  options,
  value,
  onChange,
}: {
  options: UploaderOption[];
  value: string;
  onChange: (uid: string) => void;
}) {
  return (
    <label className="field ws-filter">
      <span className="field-label">Name</span>
      <select
        className="text-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">All people</option>
        {options.map((o) => (
          <option key={o.uid} value={o.uid}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}
